import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import sh from "bundles/common/utils/sh";
import consumer from "bundles/Internal/channels/consumer";

const TRACKABLE_PATH_BUILDERS = {
  Candidate: (id) => `/internal/candidates/${id}`,
  ProfilePipeline: (id) => `/internal/pipelines/${id}`,
  JobOffer: (id) => `/internal/job_offers/${id}`,
  JobApplication: (id) => `/internal/job_applications/${id}`,
  Company: (id) => `/internal/companies/${id}`,
  Recruiter: (id) => `/internal/recruiters/${id}`,
  ExternalProfile: (id) => `/internal/external_profiles?profile_id=${id}`,
};

function activityTarget(act) {
  if (act.type === "internal_activity") {
    const build = TRACKABLE_PATH_BUILDERS[act.trackable?.type];
    return build && act.trackable?.id ? build(act.trackable.id) : null;
  }
  return act.external_profile?.id
    ? `/internal/external_profiles?profile_id=${act.external_profile.id}`
    : null;
}

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.4; }
`;

function relativeTime(dateStr) {
  if (!dateStr) return null;
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return null;
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function activityActionColor(action) {
  if (action === "whitelisted" || action === "created" || action === "unarchived") return "hsl(142, 71%, 45%)";
  if (action === "blacklisted" || action === "deleted") return "hsl(0, 72%, 51%)";
  return "hsl(150, 10%, 46%)";
}

function activityActionLabel(action) {
  if (!action) return "";
  return action.replace(/_/g, " ");
}

function trackableTypeLabel(type) {
  if (!type) return "";
  const map = {
    Candidate: "candidate",
    ExternalProfile: "profile",
    ProfilePipeline: "pipeline",
    JobApplication: "application",
    JobOffer: "job offer",
    Company: "company",
  };
  return map[type] || type.replace(/([A-Z])/g, " $1").trim().toLowerCase();
}

// For actions like "created" or "deleted" where the type adds useful context
const ACTIONS_NEEDING_TYPE = new Set(["created", "deleted", "unarchived"]);

function buildActivityText(act) {
  const action = activityActionLabel(act.action);
  const label = act.trackable?.label;
  const type = act.trackable?.type;
  const needsType = ACTIONS_NEEDING_TYPE.has(act.action) && type;

  return { action, label, typeLabel: needsType ? trackableTypeLabel(type) : null };
}

function outcomeLabel(outcome) {
  if (!outcome) return outcome;
  return outcome.replace(/_/g, " ");
}

function outcomeDotColor(outcome) {
  if (outcome === "interested" || outcome === "signed_up") return "hsl(142, 71%, 45%)";
  if (outcome === "sign_up_later") return "hsl(262, 65%, 55%)";
  if (outcome === "follow_up_today" || outcome === "follow_up_later") return "hsl(38, 92%, 50%)";
  if (outcome === "no_answer" || outcome === "voicemail") return "hsl(0, 72%, 51%)";
  if (outcome === "applied") return "hsl(217, 91%, 55%)";
  if (outcome === "email_sent") return "hsl(217, 91%, 55%)";
  return "hsl(150, 10%, 46%)";
}

const MAX_VISIBLE = 8;

const ActivityFeedCard = ({ initialActivities } = {}) => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState(initialActivities ?? []);
  const subscriptionRef = useRef(null);

  useEffect(() => {
    if (initialActivities !== undefined) {
      setActivities(initialActivities);
      return;
    }
    sh.get("/internal/coordinator/dashboard/static")
      .then((res) => {
        setActivities(Array.isArray(res.data.activities) ? res.data.activities : []);
      })
      .catch(() => {});
  }, [initialActivities]);

  useEffect(() => {
    subscriptionRef.current = consumer.subscriptions.create(
      { channel: "InternalActivityChannel" },
      {
        received(data) {
          setActivities((prev) => [data, ...prev]);
        },
      },
    );
    return () => {
      subscriptionRef.current?.unsubscribe();
    };
  }, []);

  const visibleActivities = activities.slice(0, MAX_VISIBLE);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "0.75rem",
        border: "1px solid hsl(140 8% 88%)",
        overflow: "hidden",
        backgroundColor: "#ffffff",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: "1px solid hsl(140 8% 88%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            color: "hsl(150 30% 12%)",
          }}
        >
          Activity feed
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            backgroundColor: "hsla(142, 71%, 45%, 0.1)",
            px: "10px",
            py: "4px",
            borderRadius: "999px",
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: "hsl(142, 71%, 45%)",
              animation: `${pulse} 1.5s ease-in-out infinite`,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: 10,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(142, 71%, 45%)",
            }}
          >
            Live
          </Typography>
        </Box>
      </Box>

      {/* Feed */}
      <Box>
        {visibleActivities.length === 0 ? (
          <Typography
            sx={{
              fontSize: 14,
              color: "hsl(150 10% 46%)",
              textAlign: "center",
              py: 4,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
            }}
          >
            No recent activity
          </Typography>
        ) : (
          visibleActivities.map((act, idx) => {
            const isInternalActivity = act.type === "internal_activity";
            const dotColor = isInternalActivity
              ? activityActionColor(act.action)
              : outcomeDotColor(act.outcome);
            const target = activityTarget(act);

            return (
              <Box
                key={act.id || `ia-${idx}`}
                onClick={target ? () => navigate(target) : undefined}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  px: 2.5,
                  py: "10px",
                  borderTop: idx === 0 ? "none" : "1px solid hsl(140 8% 88%)",
                  cursor: target ? "pointer" : "default",
                  transition: "background-color 0.15s ease",
                  ...(target && { "&:hover": { backgroundColor: "hsl(140 8% 96%)" } }),
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: dotColor,
                    flexShrink: 0,
                    mt: "4px",
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {isInternalActivity ? (() => {
                    const { action, label, typeLabel } = buildActivityText(act);
                    return (
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                          color: "hsl(150 30% 12%)",
                          lineHeight: 1.5,
                        }}
                      >
                        <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                          {act.internal_user?.full_name}
                        </Box>
                        {" "}
                        <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: dotColor }}>
                          {action}
                        </Box>
                        {typeLabel && (
                          <Box component="span" sx={{ color: "hsl(150 10% 46%)" }}>
                            {" "}{typeLabel}
                          </Box>
                        )}
                        {label && (
                          <>
                            {" "}
                            <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                              {label}
                            </Box>
                          </>
                        )}
                        {act.metadata?.reasons?.length > 0 && (
                          <Box component="span" sx={{ color: "hsl(150 10% 46%)" }}>
                            {" · "}{act.metadata.reasons.join(", ")}
                          </Box>
                        )}
                      </Typography>
                    );
                  })() : (
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                        color: "hsl(150 30% 12%)",
                        lineHeight: 1.5,
                      }}
                    >
                      <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                        {act.internal_user?.full_name}
                      </Box>
                      {" logged "}
                      <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: dotColor }}>
                        {outcomeLabel(act.outcome)}
                      </Box>
                      {" — "}
                      {act.external_profile?.full_name}
                      {act.external_profile?.desired_title && (
                        <Box component="span" sx={{ color: "hsl(150 10% 46%)" }}>
                          {" · "}{act.external_profile.desired_title}
                        </Box>
                      )}
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      color: "hsl(150 10% 46%)",
                      mt: "2px",
                    }}
                  >
                    {relativeTime(act.created_at) || "just now"}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Paper>
  );
};

export default ActivityFeedCard;
